import React, { useState } from 'react'
import {
    catchOrderConfirmError,
    initiateOrderAuth,
    retrieveOrder,
    submitVerificationCode
} from '../../services/api/consumer'
import { ConsumerOrderDetails } from '../../model'
import { Controller, useForm } from 'react-hook-form'
import Button from '../../components/Button/Button'
import DynamicFaIcon from '../../components/DynamicFaIcon/DynamicFaIcon'
import OtpInput from '../../components/OtpInput/OtpInput'
import DefaultLoader from '../../components/Loaders/DefaultLoader'
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage'

type EmailFormData = {
    emailAddress: string
}

type VerificationFormData = {
    verificationCode: string
}

type VerifyStep = 'email' | 'code'

interface ConfirmOrderVerifyProps {
    token?: string
    onVerified: (res: ConsumerOrderDetails) => void
}

const INVALID_LINK_ERROR_MESSAGE = 'Deze bevestigingslink is ongeldig. Controleer de link uit de e-mail.'
const INVALID_LINK_OR_EMAIL_ERROR_MESSAGE =
    'Deze link of dit e-mailadres klopt niet. Controleer je e-mailadres en probeer opnieuw.'
const REQUEST_CODE_ERROR_MESSAGE =
    'Er is een fout opgetreden bij het versturen van de verificatiecode. Probeer het opnieuw.'

const isInvalidOrderAccessError = (error: unknown) => {
    if (!(error instanceof Error)) {
        return false
    }

    return (
        error.message.includes('order not found for token') ||
        error.message.includes('record not found') ||
        error.message.includes('email does not match order')
    )
}

const resolveStartAuthErrorMessage = (error: unknown) => {
    if (isInvalidOrderAccessError(error)) {
        return INVALID_LINK_OR_EMAIL_ERROR_MESSAGE
    }

    return REQUEST_CODE_ERROR_MESSAGE
}

const ConfirmOrderVerify: React.FunctionComponent<ConfirmOrderVerifyProps> = ({
    token,
    onVerified
}: ConfirmOrderVerifyProps) => {
    const {
        register,
        formState: emailFormState,
        handleSubmit: handleEmailSubmit,
        reset: resetEmailForm
    } = useForm<EmailFormData>({
        defaultValues: {
            emailAddress: ''
        },
        mode: 'onChange',
        reValidateMode: 'onChange'
    })
    const {
        control,
        formState: verificationFormState,
        handleSubmit: handleVerificationSubmit,
        reset: resetVerificationForm
    } = useForm<VerificationFormData>({
        defaultValues: {
            verificationCode: ''
        },
        mode: 'onChange',
        reValidateMode: 'onChange'
    })
    const [step, setStep] = useState<VerifyStep>('email')
    const [isRequestingCode, setIsRequestingCode] = useState(false)
    const [isVerifyingCode, setIsVerifyingCode] = useState(false)
    const [verificationEmail, setVerificationEmail] = useState('')
    const [error, setError] = useState<string | null>(null)

    const isEmailStep = step === 'email'
    const isCodeStep = step === 'code'

    const requestVerificationCode = async (rawEmailAddress: string) => {
        const emailAddress = rawEmailAddress.trim()
        if (!emailAddress) {
            return false
        }

        if (!token) {
            setError(INVALID_LINK_ERROR_MESSAGE)
            return false
        }

        setIsRequestingCode(true)
        try {
            setError(null)
            await initiateOrderAuth(token, emailAddress)
            setVerificationEmail(emailAddress)
            setStep('code')
            return true
        } catch (authError) {
            if (!isInvalidOrderAccessError(authError)) {
                console.error('Error initiating order auth:', authError)
            }
            setError(resolveStartAuthErrorMessage(authError))
            return false
        } finally {
            setIsRequestingCode(false)
        }
    }

    const onSendVerificationCode = async (data: EmailFormData) => {
        const hasChallenge = await requestVerificationCode(data.emailAddress)
        if (!hasChallenge) {
            return
        }

        resetVerificationForm({ verificationCode: '' })
    }

    const onVerifyOrderAccess = async (data: VerificationFormData) => {
        if (!verificationEmail) {
            setStep('email')
            resetEmailForm({ emailAddress: '' })
            setError('Vul eerst het e-mailadres in dat bij de bestelling hoort.')
            return
        }

        try {
            setError(null)
            setIsVerifyingCode(true)

            const verificationResult = await submitVerificationCode(data.verificationCode.trim())
            if (!verificationResult.isSignedIn) {
                throw new Error('Verification code is not accepted.')
            }

            const res = await retrieveOrder()
            onVerified(res)
        } catch (retrieveError) {
            setError(catchOrderConfirmError(retrieveError))
        } finally {
            setIsVerifyingCode(false)
        }
    }

    const onResendCode = async () => {
        if (!verificationEmail) {
            return
        }
        await requestVerificationCode(verificationEmail)
    }

    return (
        <>
            <div className='flex flex-col space-y-2'>
                <h3 className='text-2xl text-center'>Order bevestigen</h3>
                {isEmailStep && !isRequestingCode && (
                    <p className='text-xs text-[--tb-textLight] text-center'>
                        Vul het e-mailadres in dat bij deze bestelling hoort.
                    </p>
                )}
                {isRequestingCode && (
                    <div className='w-full flex items-center justify-center py-8 min-h-[175px]'>
                        <DefaultLoader loadingMessage='Verificatiecode wordt verzonden...' />
                    </div>
                )}
                {isCodeStep && !isRequestingCode && (
                    <p className='text-xs text-[--tb-textLight] text-center'>
                        De verificatiecode is verzonden naar <span className='font-medium'>{verificationEmail}</span>.
                    </p>
                )}
            </div>

            {isEmailStep && !isRequestingCode && (
                <form onSubmit={handleEmailSubmit(onSendVerificationCode)} className='flex flex-col space-y-4'>
                    <div className='flex flex-col space-y-1 text-center'>
                        <label
                            htmlFor='emailAddress'
                            className='text-sm font-medium text-[--tb-mainHeading] mb-1 block text-center'
                        >
                            E-mailadres
                        </label>
                        <input
                            {...register('emailAddress', {
                                required: 'E-mailadres is verplicht',
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'Ongeldig e-mailadres'
                                }
                            })}
                            id='emailAddress'
                            type='email'
                            autoComplete='email'
                            placeholder='Vul je e-mailadres in'
                            disabled={isVerifyingCode || isRequestingCode}
                            className='w-full'
                        />
                        <ErrorMessage
                            message={
                                typeof emailFormState.errors.emailAddress?.message === 'string'
                                    ? emailFormState.errors.emailAddress.message
                                    : undefined
                            }
                        />
                    </div>

                    <div className='flex flex-col items-center justify-between gap-3 mb-4 w-full'>
                        {error && <p className='text-red-700 text-sm text-center'>{error}</p>}
                        <Button
                            className='w-full md:max-w-sm'
                            type='submit'
                            disabled={!emailFormState.isValid || isVerifyingCode || isRequestingCode}
                        >
                            {isRequestingCode ? (
                                <>
                                    <DynamicFaIcon iconName='spinner' className='mr-2' spin />
                                    Code verzenden..
                                </>
                            ) : (
                                <span>Verificatiecode sturen</span>
                            )}
                        </Button>
                    </div>
                </form>
            )}

            {isCodeStep && !isRequestingCode && (
                <form
                    onSubmit={handleVerificationSubmit(onVerifyOrderAccess)}
                    className='flex flex-col space-y-4 !mt-2'
                >
                    <Controller
                        name='verificationCode'
                        control={control}
                        rules={{
                            required: 'Voer de verificatiecode in',
                            pattern: {
                                value: /^\d{6}$/,
                                message: 'Voer een geldige 6-cijferige code in'
                            }
                        }}
                        render={({ field }) => (
                            <OtpInput
                                id='verificationCode'
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={isRequestingCode || isVerifyingCode}
                                onComplete={handleVerificationSubmit(onVerifyOrderAccess)}
                            />
                        )}
                    />
                    <div className='flex flex-col items-center justify-between gap-3 mb-4 w-full'>
                        <ErrorMessage
                            message={
                                typeof verificationFormState.errors.verificationCode?.message === 'string'
                                    ? verificationFormState.errors.verificationCode.message
                                    : undefined
                            }
                        />
                        {error && <p className='text-red-700 text-sm text-center'>{error}</p>}
                        <Button
                            className='w-full md:max-w-sm'
                            type='submit'
                            disabled={!verificationFormState.isValid || isVerifyingCode || isRequestingCode}
                        >
                            {isVerifyingCode ? (
                                <>
                                    <DynamicFaIcon iconName='spinner' className='mr-2' spin />
                                    Valideren..
                                </>
                            ) : (
                                <span>Volgende stap</span>
                            )}
                        </Button>
                        <div className='text-xs text-center'>
                            Geen code ontvangen?{' '}
                            <button
                                type='button'
                                onClick={() => void onResendCode()}
                                disabled={isRequestingCode || isVerifyingCode}
                                className='text-[--tb-linkColor] underline disabled:no-underline disabled:opacity-50'
                            >
                                {isRequestingCode ? 'Code verzenden..' : 'Nieuwe code sturen'}
                            </button>
                        </div>
                    </div>
                </form>
            )}
        </>
    )
}
export default ConfirmOrderVerify
